/**
 * Helpers for Pydantic Decimal fields that cross the API boundary.
 *
 * Backend Pydantic `Decimal` serializes as a JSON string (to preserve
 * precision) but its OpenAPI spec declares the field as `number | string`.
 * Generated TypeScript types reflect this union. Call sites must narrow
 * the value before using it — use these helpers at the edges.
 *
 * Usage:
 *     import { parseDecimal, formatCents } from "@bw/shared/utils";
 *     const amount = parseDecimal(org.commission_rate);  // number
 *     const display = formatCents(amount);              // "$22.50"
 */

/**
 * Match plain decimal literals only: `42`, `3.14`, `.5`, `5.`, `-5.5`, `100.00`.
 * Rejects hex (`0x2a`), octal (`0o52`), binary (`0b101`), exponent (`1e3`),
 * leading `+` (`+1.5`), numeric separators (`1_000`), thousands commas
 * (`1,234.56`), and `Infinity` / `-Infinity`. Number() accepts all of these;
 * we do not — admin commission-rate inputs pasting `"1e3"` would otherwise
 * become a 100,000% take rate.
 */
const DECIMAL_RE = /^-?(\d+\.?\d*|\.\d+)$/;

/**
 * Parse a Decimal field (number | string) from the API into a finite number.
 * Returns null for null/undefined, non-finite numbers (NaN, ±Infinity),
 * empty strings, and strings that aren't plain decimal literals (see
 * DECIMAL_RE above). Uses `Number()` with a regex gate — `parseFloat()` is
 * prefix-lenient (`"42abc"` → 42) and `Number()` alone accepts too many
 * syntaxes (hex, exponent, etc.). Both silently corrupt money values.
 */
export function parseDecimal(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  const trimmed = value.trim();
  if (!DECIMAL_RE.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Format a dollar amount (as a number) for display.
 * Accepts number | string to handle raw API values. Returns "—" for
 * unparseable values OR invalid currency codes (Intl throws RangeError
 * for "", "USDUSD", trailing-space codes, etc. — the UI should not crash
 * on bad data from the API).
 */
export function formatMoney(
  value: number | string | null | undefined,
  currency = "USD",
): string {
  const n = parseDecimal(value);
  if (n === null) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(n);
  } catch {
    return "—";
  }
}

/**
 * Convert a dollar amount to cents (integer) for sending to Stripe.
 *
 * Uses `.toFixed(2)` to absorb float underflow at the midpoint: `1.005 * 100`
 * evaluates to `100.49999999999999`, so `Math.round` alone returns 100 cents
 * — an off-by-one-cent money bug. Re-stringifying at the hundredth puts us
 * at the true midpoint before rounding.
 */
export function toCents(value: number | string | null | undefined): number | null {
  const n = parseDecimal(value);
  if (n === null) return null;
  return Math.round(Number((n * 100).toFixed(2)));
}

/**
 * Format a percentage rate (0-1 decimal) for display as "XX%".
 * Accepts number | string to handle API Decimals.
 */
export function formatPercent(
  value: number | string | null | undefined,
  fractionDigits = 0,
): string {
  const n = parseDecimal(value);
  if (n === null) return "—";
  return `${(n * 100).toFixed(fractionDigits)}%`;
}
